import React from 'react';
import { useLocation, useHistory, matchPath } from 'react-router';

// Types
import { UpdateAiReportType } from 'Shared/types/UpdateAiReportType.type';

// Constants
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useUpdateAiReport } from 'Shared/utils/hooks/use-update-ai-report';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Components
import { WpButton } from 'Wpds/components/button';
import WpIcon from 'Wpds/components/icon/WpIcon';
import {
  WpDropdown,
  WpDropdownToggle,
  WpDropdownMenu,
  WpDropdownMenuItem,
  WpDropdownMenuDivider,
} from 'Wpds/components/dropdown';
import ShareReportModal from '../ShareReportModal/ShareReportModal';
import ConfirmModal from 'Shared/components/ConfirmModal/ConfirmModal';

type Props = {
  report: UpdateAiReportType;
  handleEditClick?: Function;
};

const ReportDropdown = ({ report, handleEditClick }: Props) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { handleDeleteReport } = useUpdateAiReport({ slug: report.slug });
  const { slug: currentUserSlug } = useCurrentUser();

  const [isShareModalOpen, toggleIsShareModalOpen] = useToggle(false);
  const [isDeleteModalOpen, toggleIsDeleteModalOpen] = useToggle(false);
  const [isDeleting, toggleIsDeleting] = useToggle(false);

  const isReportPage = matchPath(pathname, { path: PageUrlsConstants.UPDATE_AI_REPORT_PAGE });
  const isCurrentUserOwner = currentUserSlug === report.ownerSlug;

  const handleDeleteClick = () => {
    toggleIsDeleting();
    handleDeleteReport().then(() => {
      history.push(PageUrlsConstants.UPDATE_AI_PAGE);
    });
  };

  const handlePrintClick = () => {
    setTimeout(() => window.print(), 10);
  };

  return (
    <>
      <div onClick={e => e.stopPropagation()}>
        <WpDropdown placement="bottom-end">
          <WpDropdownToggle>
            <WpButton size="sm" variant="tertiary">
              <WpIcon name="ellipsis-h-alt" />
            </WpButton>
          </WpDropdownToggle>
          <WpDropdownMenu>
            {isCurrentUserOwner && isReportPage && handleEditClick && (
              <WpDropdownMenuItem onClick={handleEditClick}>
                <WpIcon name="pencil" />
                Edit
              </WpDropdownMenuItem>
            )}
            <WpDropdownMenuItem onClick={toggleIsShareModalOpen}>
              <WpIcon name="share" />
              Share
            </WpDropdownMenuItem>
            {isReportPage && (
              <WpDropdownMenuItem onClick={handlePrintClick}>
                <WpIcon name="print" />
                Print
              </WpDropdownMenuItem>
            )}
            {isCurrentUserOwner && (
              <>
                <WpDropdownMenuDivider />
                <WpDropdownMenuItem onClick={toggleIsDeleteModalOpen} color="danger">
                  <WpIcon name="trash" />
                  Delete
                </WpDropdownMenuItem>
              </>
            )}
          </WpDropdownMenu>
        </WpDropdown>
        <ShareReportModal isOpen={isShareModalOpen} toggleIsOpen={toggleIsShareModalOpen} slug={report.slug} />
        <ConfirmModal
          isOpen={isDeleteModalOpen}
          onToggle={toggleIsDeleteModalOpen}
          title="Delete Report"
          message="Are you sure you want to delete this report?"
          isProcessing={isDeleting}
          onConfirmClick={handleDeleteClick}
          confirmButtonLabel="Delete"
        />
      </div>
    </>
  );
};

export default ReportDropdown;
